// Real-Time Notification System
//
// The notification BADGE and dropdown LIST are rendered SERVER-SIDE (templ, in
// the request's language) and delivered via HTMX out-of-band swaps:
//   - on page load + dropdown open: <details> hx-get /api/v1/notifications/fragment
//   - on a friend event (live):     SSE "notification_update" on /api/v1/stream/user/events
// There is NO client-side JSON rendering or window.t translation of the list.
//
// This JS only handles: (1) SSE-first toast, (2) deduped poller fallback toast,
// (3) the mobile bell toggle + outside-click close, (4) mark-read POSTs (CSRF)
// then an HTMX refresh of the server-rendered fragment.

// ---------------------------------------------------------------------------
// Toast dedup state (bounded set; cleared wholesale at the limit)
// ---------------------------------------------------------------------------
const TOAST_ID_LIMIT = 200;
const shownToastIds = new Set();

function recordToastShown(id) {
    if (!id) return;
    if (shownToastIds.size >= TOAST_ID_LIMIT) shownToastIds.clear();
    shownToastIds.add(id);
}

function wasToastShown(id) {
    return id && shownToastIds.has(id);
}

// ---------------------------------------------------------------------------
// Re-fetch the server-rendered badge + list (HTMX OOB). Used after the user
// marks notifications read. The response carries hx-swap-oob fragments, so a
// 'none' swap is correct; HTMX still applies the OOB elements.
// ---------------------------------------------------------------------------
function refreshNotifications() {
    if (window.htmx) {
        window.htmx.ajax('GET', '/api/v1/notifications/fragment', { target: 'body', swap: 'none' });
    }
}

// Re-sync the badge + list when sse-resilience.js signals the real-time channel
// recovered (tab re-shown, network back, heartbeat lost). Guarded to the
// authenticated header so unauthenticated pages don't fire a doomed request.
document.addEventListener('realtime-resync', () => {
    if (document.getElementById('notification-btn')) refreshNotifications();
});

// NOTE: no client-side i18n. The notification badge + list are server-rendered
// (HTMX OOB). The poller stream (/api/v1/stream/notifications) and the SSE
// "toast" event already deliver text translated server-side in the viewer's
// locale, so the toast just shows notification.title / notification.message
// (or the toast event's message) verbatim.

// ---------------------------------------------------------------------------
// PRIMARY toast source – the header's htmx SSE connection to
// /api/v1/stream/user/events (SSE-first toast).
//
// The badge/dropdown list already open ONE htmx-managed EventSource on this
// endpoint (header.templ: sse-connect, sse-swap="notification_update"). The
// server emits the `toast` event on that SAME stream, so rather than opening a
// second EventSource we piggyback on htmx's connection: on htmx:sseOpen we grab
// the underlying EventSource (event.detail.source) and attach our own `toast`
// listener. htmx recreates the source and re-fires htmx:sseOpen on reconnect,
// so this re-wires automatically; the stable listener reference keeps it
// idempotent if onopen fires more than once for the same source.
// ---------------------------------------------------------------------------
function handleToastEvent(event) {
    try {
        const data = JSON.parse(event.data);
        const notifId = data.notificationId || null;
        if (wasToastShown(notifId)) return;
        recordToastShown(notifId);
        const message = data.message || '';
        const type = data.type || 'info';
        if (message && typeof window.showToast === 'function') {
            window.showToast(message, type);
        }
    } catch (err) {
        console.warn('Failed to parse toast SSE event:', err, event.data);
    }
}

// Registered at load time so it is ready before htmx opens the stream.
// Only wires the user-events source (the room-events SSE also fires sseOpen).
document.addEventListener('htmx:sseOpen', (event) => {
    const source = event.detail && event.detail.source;
    if (!source || !source.url || source.url.indexOf('/api/v1/stream/user/events') === -1) return;
    source.addEventListener('toast', handleToastEvent); // idempotent: stable fn reference
});

// ---------------------------------------------------------------------------
// FALLBACK EventSource – /api/v1/stream/notifications (2 s poller, toast only)
// ---------------------------------------------------------------------------
let notificationEventSource = null;
let notificationReconnectTimeout = null;

function connectNotificationStream() {
    if (!getUserIdFromSession()) return;
    if (notificationEventSource) notificationEventSource.close();

    notificationEventSource = new EventSource('/api/v1/stream/notifications');

    notificationEventSource.addEventListener('notification', (event) => {
        try {
            const notification = JSON.parse(event.data);
            // Badge/list are server-pushed OOB — poller is toast-only fallback.
            showBrowserNotification(notification);
            const notifId = notification.id || null;
            if (!wasToastShown(notifId)) {
                recordToastShown(notifId);
                showToastNotification(notification);
            }
        } catch (error) {
            console.error('Error processing notification:', error);
        }
    });

    notificationEventSource.addEventListener('ping', () => {});

    notificationEventSource.onerror = () => {
        notificationEventSource.close();
        if (notificationReconnectTimeout) clearTimeout(notificationReconnectTimeout);
        notificationReconnectTimeout = setTimeout(connectNotificationStream, 5000);
    };
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
    if (!document.getElementById('notification-btn')) return;
    // Primary SSE-first toasts piggyback on htmx's user-events connection via
    // the htmx:sseOpen listener above — no separate EventSource needed here.
    connectNotificationStream(); // fallback: deduped poller toasts
    // Badge + list load via the bell's hx-get (hx-trigger="load, toggle").
});

function getUserIdFromSession() {
    const userMeta = document.querySelector('meta[name="user-id"]');
    if (userMeta) return userMeta.content;
    const btn = document.getElementById('notification-btn');
    if (btn && btn.dataset.userId) return btn.dataset.userId;
    return document.getElementById('notification-btn') ? 'authenticated' : '';
}

// ---------------------------------------------------------------------------
// Browser notification
// ---------------------------------------------------------------------------
function showBrowserNotification(notification) {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
        new Notification(notification.title, {
            body: notification.message,
            icon: '/static/favicon.svg',
            badge: '/static/favicon.svg',
            tag: notification.id
        });
    } else if (Notification.permission === 'default') {
        Notification.requestPermission();
    }
}

// ---------------------------------------------------------------------------
// Fallback toast (only when the SSE-first toast didn't fire). Routes through
// the SINGLE shared Toast system (ui-utils window.showToast → fixed, styled,
// floating .toast-container) — no separate legacy markup, so it never renders
// as an unstyled block in the page flow. Text is already server-localized.
// ---------------------------------------------------------------------------
function showToastNotification(notification) {
    if (typeof window.showToast !== 'function') return;
    const msg = notification.message || notification.title || '';
    if (msg) window.showToast(msg, 'info');
}

// ---------------------------------------------------------------------------
// Notification panel — custom <div>-based dropdown (Figma v2 redesign).
// Open/close is driven by the `hidden` attribute on #notification-panel.
// ---------------------------------------------------------------------------
window.NotificationPanel = {
    toggle() {
        const panel = document.getElementById('notification-panel');
        const btn   = document.getElementById('notification-btn');
        if (!panel) return;
        const isOpen = !panel.hidden;
        panel.hidden = isOpen;
        if (btn) btn.setAttribute('aria-expanded', String(!isOpen));
    },
    close() {
        const panel = document.getElementById('notification-panel');
        const btn   = document.getElementById('notification-btn');
        if (panel) panel.hidden = true;
        if (btn)   btn.setAttribute('aria-expanded', 'false');
    }
};

// ---------------------------------------------------------------------------
// Join-accepted modal — open automatically once HTMX swaps it into the mount.
// Bind to `document` (not document.body): the bundle runs in <head>, so
// document.body can still be null at module-eval time. htmx:afterSettle bubbles
// to document, and e.target is still the settled element.
// ---------------------------------------------------------------------------
document.addEventListener('htmx:afterSettle', (e) => {
    if (e.target && e.target.id === 'join-accepted-modal-mount') {
        const dlg = e.target.querySelector('dialog');
        if (dlg && window.couple && window.couple.openModal) {
            window.couple.openModal(dlg);
        } else if (dlg) {
            // Fallback: use native <dialog> API
            dlg.showModal();
        }
    }
});

// Close on outside click
document.addEventListener('click', (e) => {
    const nav = document.getElementById('notification-nav');
    if (!nav) return;
    const panel = document.getElementById('notification-panel');
    if (!panel || panel.hidden) return;
    if (!nav.contains(e.target)) window.NotificationPanel.close();
});

// ---------------------------------------------------------------------------
// CSRF header for the mark-read POSTs (the /api/v1 group enforces CSRF;
// the _csrf cookie is HttpOnly, so use the <meta> token).
// ---------------------------------------------------------------------------
function csrfHeaders() {
    const t = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    return t ? { 'X-CSRF-Token': t } : {};
}

async function handleNotificationClick(notificationId, link) {
    try {
        await fetch(`/api/v1/notifications/${notificationId}/read`, {
            method: 'POST',
            headers: csrfHeaders()
        });
    } catch (error) {
        console.error('Failed to mark notification as read:', error);
    }
    if (link) {
        window.location.href = link; // full nav re-renders everything server-side
    } else {
        refreshNotifications();
    }
}

async function markAllRead() {
    try {
        const response = await fetch('/api/v1/notifications/read-all', {
            method: 'POST',
            headers: csrfHeaders()
        });
        if (response.ok) refreshNotifications();
    } catch (error) {
        console.error('Failed to mark all as read:', error);
    }
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------
window.addEventListener('beforeunload', () => {
    // The user-events stream is owned by htmx (closed on navigation); only the
    // poller EventSource is opened directly here.
    if (notificationEventSource) notificationEventSource.close();
    if (notificationReconnectTimeout) clearTimeout(notificationReconnectTimeout);
});
